import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, Settings, Users, BookOpen, DollarSign, Calendar, FileText, 
  Library, Package, Building, Bus, PieChart, MessageSquare, UserPlus,
  ClipboardList, GraduationCap, Award, UsersRound, Briefcase, UserCheck,
  FileStack, Globe, CreditCard, ChevronDown, ChevronRight, Menu, X, Shield, Database, TrendingUp,
  Laptop, Handshake, Clock, Trash2, Mail, ShoppingCart
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import useModuleAccess from '@/hooks/useModuleAccess';

const menuItems = [
  { icon: Home, label: 'Dashboard', path: '/dashboard' },
  { icon: Clock, label: 'Timezone Settings', path: '/settings/timezone', highlight: true },
  { icon: DollarSign, label: 'Currency Settings', path: '/settings/currency', highlight: true },
  { icon: Database, label: 'Daily Backup', path: '/admin/backup' },
  { icon: Trash2, label: 'Delete Logs', path: '/admin/delete-logs', highlight: true },
  { icon: Settings, label: 'Profile Settings', path: '/profile' },
  { icon: Building, label: 'College Settings', path: '/college-settings' },
  {
    icon: Settings,
    label: 'Master Settings',
    submenu: [
      { label: 'Courses', path: '/master-settings/courses' },
      { label: 'Semesters', path: '/master-settings/semesters' },
      { label: 'Subjects', path: '/master-settings/subjects' },
      { label: 'Method Subjects (B.Ed)', path: '/master-settings/method-subjects' },
      { label: 'Receipt Terms & Conditions', path: '/master-settings/receipt-terms' },
      { label: 'Sessions', path: '/master-settings/sessions' },
      { label: 'Classrooms', path: '/master-settings/classrooms' },
      { label: 'Departments', path: '/master-settings/departments' },
      { label: 'Designations', path: '/master-settings/designations' },
      { label: 'Sections', path: '/master-settings/sections' },
      { label: 'Fee Structures', path: '/master-settings/fee-structures' },
      { label: 'Discounts', path: '/master-settings/discounts' },
      { label: 'Scholarships', path: '/master-settings/scholarships' },
      { label: 'Tax Management', path: '/master-settings/tax-management' },
      { label: 'General Fines', path: '/master-settings/fine-setting' },
      { label: 'Other Fees', path: '/master-settings/other-fees' },
      { label: 'Languages', path: '/master-settings/languages' },
      { label: 'Religions', path: '/master-settings/religions' },
      { label: 'Castes', path: '/master-settings/castes' },
      { label: 'Holidays', path: '/master-settings/holidays' },
      { label: 'Working Days', path: '/master-settings/working-days' },
      { label: 'Attendance Penalty', path: '/master-settings/attendance-fine', module: 'attendance' },
      { label: 'Salary Components', path: '/master-settings/salary-components', module: 'hr' },
      { label: 'Withdrawal Policy', path: '/master-settings/withdrawal-policy' },
      { label: 'Staff Attendance Settings', path: '/settings/attendance', module: 'hr' },
      { label: 'Email Settings', path: '/admin/email-settings' },
      { label: 'Payment Gateway', path: '/settings/payment-gateway' },
      { label: 'Policy Manager', path: '/settings/policies' },
      { label: 'SMS Settings', path: '/settings/sms' },
      { label: 'WhatsApp Settings', path: '/settings/whatsapp' },
    ]
  },
  {
    icon: UserPlus,
    label: 'Consultant',
    submenu: [
      { label: 'Add Consultant', path: '/consultants/add' },
      { label: 'Consultant List', path: '/consultants/list' },
      { label: 'Block Consultant', path: '/consultants/block' },
      { label: 'Pay Consultant', path: '/consultants/pay' },
      { label: 'Consultant Ledger', path: '/consultants/ledger' },
      { label: 'Business Report', path: '/consultants/business-report' },
      { label: 'Payment Report', path: '/consultants/payment-report' },
      { label: 'Student Wise Payout Report', path: '/consultants/student-payout-report' },
    ]
  },
  {
    icon: BookOpen,
    label: 'Seat Booking',
    submenu: [
      { label: 'Booking Amount Set', path: '/seat-booking/amount-set' },
      { label: 'Book Seat', path: '/seat-booking/book' },
      { label: 'Booking List', path: '/seat-booking/list' },
      { label: 'Booking Report', path: '/seat-booking/report' },
    ]
  },
  {
    icon: Users,
    label: 'Admission',
    submenu: [
      { label: 'Admission Fee Setting', path: '/admission/admission-fee-setting' },
      { label: 'Admission Hub', path: '/admission/hub', highlight: true },
      { label: 'Student Admission', path: '/students/admission' },
      { label: 'Bulk Admission Upload', path: '/students/bulk-admission' },
      { label: 'Online Admission Form Link', path: '/admission/online-form-link' },
      { label: 'Document Settings', path: '/admission/document-settings' },
      { label: 'Block Student', path: '/admission/block-student' },
      { label: 'Student Withdrawal', path: '/admission/student-withdrawal' },
      { label: 'I-Card Builder', path: '/admission/icard-builder' },
      { label: 'I Card Report', path: '/admission/icard-report' },
      { label: 'Print I Card', path: '/admission/print-icard' },
      { label: 'Bonafide Certificate', path: '/admission/bonafide-certificate' },
      { label: 'Course Completion Certificate', path: '/admission/course-completion-certificate' },
      { label: 'Manual Certificate Print', path: '/certificates/manual-print' },
      { label: 'Certificate History', path: '/certificates/history' },
      { label: 'Certificate Settings', path: '/certificates/settings' },
      { label: 'Admission Reports', path: '/admission/reports' },
      { label: 'Student Register', path: '/admission/student-register' },
      { label: 'Student List', path: '/students/list' },
      { label: 'Withdrawal History', path: '/admission/withdrawal-list' },
    ]
  },
  {
    icon: DollarSign,
    label: 'Fees Management',
    submenu: [
      { label: 'Fee Installment Setting', path: '/fees/installment-setting' },
      { label: 'Payment Links', path: '/fees/payment-links' },
      { label: 'Pay Fee', path: '/fees/pay-fee' },
      { label: 'Pay Other Fee', path: '/fees/pay-other-fee' },
      { label: 'Student Ledger', path: '/fees/student-ledger' },
      { label: 'Fee Reminders', path: '/fees/reminders' },
      { label: 'Clearance Certificate', path: '/fees/clearance-certificate' },
      { label: 'Caution Money Due', path: '/fees/caution-money/due' },
      { label: 'Collect Caution Money', path: '/fees/caution-money/pay' },
      { label: 'Refund Caution Money', path: '/fees/caution-money/refund' },
      { label: 'Fee Installment Report', path: '/fees/installment-report' },
      { label: 'Fees Report', path: '/fees/report' },
      { label: 'Pay Other Fee Report', path: '/fees/pay-other-fee-report' },
      { label: 'Due Fees Report', path: '/fees/due-report' },
      { label: 'Discount Report', path: '/fees/discount-report' },
      { label: 'Scholarship Report', path: '/fees/scholarship-report' },
      { label: 'Collection Day Book', path: '/fees/collection-daybook' },
      { label: 'Caution Money Report', path: '/fees/caution-money/report' },
    ]
  },
  {
    icon: Calendar,
    label: 'Student Attendance',
    submenu: [
      { label: 'Class Attendance', path: '/routine/class-attendance' },
      { label: 'Pay Attendance Fine', path: '/attendance/pay-fine' },
      { label: 'Clearance Certificate', path: '/attendance/clearance' },
      { label: 'Fine Collection Report', path: '/attendance/fine-collection' },
      { label: 'Attendance Report', path: '/attendance/report' },
    ]
  },
  {
    icon: FileText,
    label: 'Routine Management',
    submenu: [
      { label: 'Setting', path: '/routine/setting' },
      { label: 'Manage Classes', path: '/routine/manage-classes' },
      { label: 'Class Schedule', path: '/routine/class-schedule' },
      { label: 'Teacher Wise Routine', path: '/routine/teacher-wise' },
      { label: 'Class Report', path: '/routine/class-report' },
      { label: 'Print Routine', path: '/routine/print' },
    ]
  },
  {
    icon: GraduationCap,
    label: 'Examination',
    submenu: [
      { label: 'Exam Type', path: '/examination/exam-type' },
      { label: 'Admit Setting', path: '/examination/admit-setting' },
      { label: 'Mark Sheet Setting', path: '/examination/marksheet-setting' },
      { label: 'Grading System', path: '/examination/grading-system' },
      { label: 'Manage Exams', path: '/examination/manage-exams' },
      { label: 'Exam Fee Payment', path: '/examination/exam-fee-payment' },
      { label: 'Exam Attendance', path: '/examination/exam-attendance' },
      { label: 'Marks Entry', path: '/examination/marks-entry' },
      { label: 'Marks Tabulation', path: '/examination/marks-tabulation' },
      { label: 'Results', path: '/examination/results' },
      { label: 'Failed Students', path: '/examination/failed-students' },
      { label: 'Promote Student', path: '/examination/promote-student' },
      { label: 'Exam Result', path: '/examination/exam-result' },
      { label: 'Admit Cards', path: '/examination/admit-cards' },
      { label: 'Promotion Report', path: '/examination/promotion-report' },
      { label: 'Exam Attendance Report', path: '/examination/exam-attendance-report' },
      { label: 'Exam Fee Report', path: '/examination/exam-fee-report' },
    ]
  },
  {
    icon: Laptop,
    label: 'LMS',
    submenu: [
      { label: 'LMS Dashboard', path: '/lms' },
      { label: 'Course Content', path: '/lms/content' },
      { label: 'Assignments', path: '/lms/assignments' },
      { label: 'Quizzes & Tests', path: '/lms/quizzes' },
      { label: 'Online Classes', path: '/lms/online-classes' },
      { label: 'Discussion Forum', path: '/lms/discussions' },
      { label: 'Review Submissions', path: '/lms/submissions' },
      { label: 'Announcements', path: '/lms/announcements' },
      { label: 'Student Analytics', path: '/lms/analytics' },
    ]
  },
  {
    icon: UsersRound,
    label: 'HR Management',
    submenu: [
      { label: 'Staff List', path: '/hr/staff-list' },
      { label: 'Staff Documents', path: '/hr/staff-documents-report' },
      { label: 'Staff Salary', path: '/hr/staff-salary' },
      { label: 'Staff Attendance', path: '/hr/attendance' },
      { label: 'Missed Checkouts', path: '/hr/missed-checkouts' },
      { label: 'Leave Approval', path: '/hr/leave-approval' },
      { label: 'Regularization Requests', path: '/hr/regularization-approval' },
      { label: 'Payroll', path: '/hr/payroll' },
      { label: 'Staff Ledger', path: '/hr/staff-ledger' },
      { label: 'Print Salary Slip', path: '/hr/print-salary-slip' },
      { label: 'Resign/ Block Staff', path: '/hr/resign-block' },
      { label: 'Print Experience Certi', path: '/hr/experience-certificate' },
      { label: 'Print Releaving', path: '/hr/releaving-letter' },
      { label: 'Staff I-Card Builder', path: '/hr/staff-icard-builder' },
      { label: 'Print Staff I-Card', path: '/hr/print-staff-icard' },
      { label: 'Attendance Report', path: '/hr/attendance-report' },
      { label: 'Payroll Report', path: '/hr/payroll-report' },
      { label: 'Staff I-Card Report', path: '/hr/staff-icard-report' },
    ]
  },
  {
    icon: UserCheck,
    label: 'Leave Manager',
    submenu: [
      { label: 'Leave Head Setting', path: '/leave/head-setting' },
      { label: 'Leave Setting', path: '/leave/setting' },
      { label: 'Leave Apply', path: '/leave/apply' },
      { label: 'My Leave', path: '/leave/my-leave' },
      { label: 'Manage Leave', path: '/leave/manage' },
      { label: 'Leave Report', path: '/leave/report' },
    ]
  },
  {
    icon: Library,
    label: 'Library Management',
    submenu: [
      { label: 'Author Entry', path: '/library/author-entry' },
      { label: 'Category Entry', path: '/library/category-entry' },
      { label: 'Publishers Entry', path: '/library/publishers-entry' },
      { label: 'Rack Entry', path: '/library/rack-entry' },
      { label: 'Books', path: '/library/books' },
      { label: 'Accession Register', path: '/library/accession-register' },
      { label: 'Issue Book', path: '/library/issue-book' },
      { label: 'Return Book', path: '/library/return-book' },
      { label: 'Issued Books', path: '/library/issued-books' },
      { label: 'Add Member', path: '/library/add-member' },
      { label: 'Member List', path: '/library/member-list' },
      { label: 'Damaged Books', path: '/library/damaged-books' },
      { label: 'Books Reports', path: '/library/books-reports' },
      { label: 'Fine Report', path: '/library/fine-report' },
    ]
  },
  {
    icon: Package,
    label: 'Inventory',
    submenu: [
      { label: 'Add Stock', path: '/inventory/add-stock' },
      { label: 'Issue Stock', path: '/inventory/issue-stock' },
      { label: 'Suppliers Entry', path: '/inventory/suppliers-entry' },
      { label: 'Purchase Order', path: '/inventory/purchase-order' },
      { label: 'Receive Item', path: '/inventory/receive-item' },
      { label: 'Pay Suppliers', path: '/inventory/pay-suppliers' },
      { label: 'Inventory Ledger', path: '/inventory/ledger' },
      { label: 'Damage Entry', path: '/inventory/damage-entry' },
      { label: 'Suppliers Report', path: '/inventory/suppliers-report' },
      { label: 'Stock Report', path: '/inventory/stock-report' },
      { label: 'Stock Use Report', path: '/inventory/stock-use-report' },
    ]
  },
  {
    icon: Building,
    label: 'Hostel Management',
    submenu: [
      { label: 'Hostel Setting', path: '/hostel/setting' },
      { label: 'Room Setting', path: '/hostel/room-setting' },
      { label: 'Warden Setting', path: '/hostel/warden-setting' },
      { label: 'Fee Setting', path: '/hostel/fee-setting' },
      { label: 'Add Student', path: '/hostel/add-student' },
      { label: 'Fee Payment', path: '/hostel/fee-payment' },
      { label: 'Student Ledger', path: '/hostel/student-ledger' },
      { label: 'Hostel Damage', path: '/hostel/damage' },
      { label: 'Hostel Fine', path: '/hostel/fine' },
      { label: 'Clearance Certificate', path: '/hostel/clearance-certificate' },
      { label: 'Fee Report', path: '/hostel/fee-report' },
    ]
  },
  {
    icon: Bus,
    label: 'Transport',
    submenu: [
      { label: 'Transport Type', path: '/transport/type' },
      { label: 'Transport Setting', path: '/transport/setting' },
      { label: 'Driver Setting', path: '/transport/driver-setting' },
      { label: 'Route Setting', path: '/transport/route-setting' },
      { label: 'Add Student', path: '/transport/add-student' },
      { label: 'Fee Payment', path: '/transport/fee-payment' },
      { label: 'Student Ledger Transport', path: '/transport/student-ledger' },
      { label: 'Transport Clearance', path: '/transport/clearance' },
      { label: 'Fee Report', path: '/transport/fee-report' },
    ]
  },
  {
    icon: DollarSign,
    label: 'Accounts',
    submenu: [
      { label: 'Income Category', path: '/accounts/income-category' },
      { label: 'Expenses Category', path: '/accounts/expense-category' },
      { label: 'Add Bank', path: '/accounts/add-bank' },
      { label: 'Cash Register', path: '/accounts/cash-register' },
      { label: 'Bank Register', path: '/accounts/bank-register' },
      { label: 'Income Management', path: '/accounts/income-management' },
      { label: 'Expense Management', path: '/accounts/expense-management' },
      { label: 'Create Suppliers', path: '/accounts/suppliers' },
      { label: 'Purchase Entry', path: '/accounts/purchase-entry' },
      { label: 'Suppliers Payment', path: '/accounts/suppliers-payment' },
      { label: 'Suppliers Ledger', path: '/accounts/suppliers-ledger' },
      { label: 'Assets Entry', path: '/accounts/assets-entry' },
      { label: 'Depreciation Setting', path: '/accounts/depreciation-setting' },
      { label: 'Liabilities Entry', path: '/accounts/liabilities-entry' },
      { label: 'Interest Setting', path: '/accounts/interest-setting' },
      { label: 'Suppliers Report', path: '/accounts/suppliers-report' },
      { label: 'Assets Report', path: '/accounts/assets-report' },
      { label: 'Income Report', path: '/accounts/income-report' },
      { label: 'Income & Expenses Report', path: '/accounts/income-expenses-report' },
      { label: 'Profit & Loss Account', path: '/accounts/profit-loss' },
      { label: 'Balance Sheet', path: '/accounts/balance-sheet' },
    ]
  },
  {
    icon: MessageSquare,
    label: 'Communication',
    submenu: [
      { label: 'Notice Board', path: '/notices' },
      { label: 'Messaging Center', path: '/communication/messages' },
      { label: 'Send Notification', path: '/notifications/send' },
      { label: 'Staff Messages', path: '/notifications/staff-messages' },
      { label: 'Send Email Students', path: '/communication/email-students' },
      { label: 'Send Email Staff', path: '/communication/email-staff' },
      { label: 'Send Email Suppliers', path: '/communication/email-suppliers' },
      { label: 'Email Logs', path: '/communication/email-logs' },
      { label: 'Send SMS Students', path: '/communication/sms-students' },
      { label: 'Send Whatsapp', path: '/communication/whatsapp' },
    ]
  },
  {
    icon: Award,
    label: 'Reports',
    submenu: [
      { label: 'Seat Booking Report', path: '/reports/seat-booking' },
      { label: 'Admission Report', path: '/reports/admission' },
      { label: 'Fees Report', path: '/reports/fees' },
      { label: 'Income Report', path: '/reports/income' },
      { label: 'Expenses Report', path: '/reports/expenses' },
      { label: 'Inventory Report', path: '/reports/inventory', module: 'inventory' },
      { label: 'Library Report', path: '/reports/library', module: 'library' },
      { label: 'Hostel Report', path: '/reports/hostel', module: 'hostel' },
      { label: 'Transport Report', path: '/reports/transport', module: 'transport' },
      { label: 'Staff Leave Report', path: '/reports/staff-leave', module: 'leave' },
      { label: 'Staff Attendance Report', path: '/reports/staff-attendance', module: 'hr' },
      { label: 'Student Attendance Report', path: '/reports/student-attendance', module: 'attendance' },
      { label: 'Salary Report', path: '/reports/salary', module: 'hr' },
      { label: 'Tax Report', path: '/reports/tax' },
      { label: 'Day Book', path: '/reports/daybook' },
      { label: 'Cash Book', path: '/reports/cashbook' },
      { label: 'Bank Book', path: '/reports/bankbook' },
      { label: 'Library Fee Report', path: '/reports/library-fee', module: 'library' },
      { label: 'Library Fine Report', path: '/reports/library-fine', module: 'library' },
      { label: 'Hostel Damage Fine', path: '/reports/hostel-damage-fine', module: 'hostel' },
      { label: 'Late Fee Report', path: '/reports/late-fee' },
      { label: 'Library Damage Report', path: '/reports/library-damage', module: 'library' },
      { label: 'Inventory Damage Report', path: '/reports/inventory-damage', module: 'inventory' },
      { label: 'Activity Log', path: '/admin/activity-log' },
    ]
  },
  {
    icon: GraduationCap,
    label: 'Placement Cell',
    submenu: [
      { label: 'Dashboard', path: '/placement' },
      { label: 'Companies', path: '/placement/companies' },
      { label: 'Company Visits', path: '/placement/visits' },
      { label: 'Job Postings', path: '/placement/jobs' },
      { label: 'Applications', path: '/placement/applications' },
      { label: 'Placement Reports', path: '/placement/reports' },
    ]
  },
  {
    icon: Award,
    label: 'NAAC',
    submenu: [
      { label: 'NAAC Dashboard', path: '/naac-dashboard' },
      { label: 'NAAC Reports', path: '/reports/naac' },
      { label: 'IQAC Meetings', path: '/iqac' },
      { label: 'IQAC Initiatives', path: '/iqac/initiatives' },
      { label: 'IQAC Feedback', path: '/iqac/feedback' },
      { label: 'AQAR', path: '/iqac/aqar' },
      { label: 'Alumni Directory', path: '/alumni' },
      { label: 'Alumni Analytics', path: '/alumni?tab=analytics' },
      { label: 'Scholarship Schemes', path: '/scholarships' },
      { label: 'Scholarship Applications', path: '/scholarships/applications' },
      { label: 'Faculty Publications', path: '/research' },
      { label: 'Research Projects', path: '/research/projects' },
      { label: 'Patents', path: '/research/patents' },
      { label: 'Workshops/FDPs', path: '/research/workshops' },
      { label: 'MoU List', path: '/mou' },
      { label: 'MoU Activities', path: '/mou/activities' },
      { label: 'MoU Analytics', path: '/mou/analytics' },
    ]
  },
  {
    icon: Briefcase,
    label: 'Front Desk',
    submenu: [
      { label: 'Visitor Log', path: '/frontdesk/visitor-log' },
      { label: 'Phone Log', path: '/frontdesk/phone-log' },
      { label: 'Enquiry List', path: '/frontdesk/enquiry' },
      { label: 'Complin List', path: '/frontdesk/complin' },
      { label: 'Postal Exchange', path: '/frontdesk/postal' },
      { label: 'Meeting Schedule', path: '/frontdesk/meeting' },
    ]
  },
  {
    icon: FileStack,
    label: 'Transcripts',
    submenu: [
      { label: 'Students Documents', path: '/transcripts/students-documents' },
      { label: 'Staff Documents', path: '/transcripts/staff-documents' },
      { label: 'Consultant Documents', path: '/transcripts/consultant-documents' },
      { label: 'Suppliers Documents', path: '/transcripts/suppliers-documents' },
    ]
  },
  {
    icon: ClipboardList,
    label: 'Ticket Management',
    submenu: [
      { label: 'Create Ticket', path: '/tickets/create' },
      { label: 'Ticket Report', path: '/tickets/report' },
      { label: 'Resolve Report', path: '/tickets/resolve' },
      { label: 'Ticket Update', path: '/tickets/update' },
      { label: 'Schedule Meeting', path: '/tickets/schedule-meeting' },
      { label: 'Meeting Report', path: '/tickets/meeting-report' },
    ]
  },
  {
    icon: Shield,
    label: 'User Management',
    submenu: [
      { label: 'All Users', path: '/user-management/all-users' },
      { label: 'Reset Password', path: '/user-management/reset-password' },
      { label: 'Block/Unblock Users', path: '/user-management/block-users' },
      { label: 'Login History', path: '/user-management/login-history' },
    ]
  },
  {
    icon: UserCheck,
    label: 'Role Management',
    submenu: [
      { label: 'Assign Roles', path: '/roles/assign' },
      { label: 'Staff by Role', path: '/roles/staff-by-role' },
      { label: 'Role Permissions', path: '/roles/permissions' },
    ]
  },
  {
    icon: CreditCard,
    label: 'Software Payment',
    submenu: [
      { label: 'My Subscription', path: '/admin/my-subscription' },
      { label: 'Upgrade Plan', path: '/admin/subscription-upgrade' },
      { label: 'Payment History', path: '/admin/software-payment' },
    ]
  },
];

const Sidebar = ({ isOpen, toggleSidebar, tenantCode: propTenantCode }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState({});
  const { isMenuVisible, isModuleEnabled, isSuperAdmin, loading: modulesLoading } = useModuleAccess();
  
  // Get tenantCode from prop or localStorage
  const tenantCode = propTenantCode || localStorage.getItem('tenant_code');
  
  // Filter menu items based on module access
  const filteredMenuItems = menuItems.filter(item => {
    // Hide tenant-specific Ticket Management from SuperAdmin (SuperAdmin has /superadmin/tickets)
    if ((isSuperAdmin || user?.role === 'SuperAdmin') && item.label === 'Ticket Management') {
      return false;
    }
    // Hide Software Payment for colleges under a Group (Group Admin pays for all)
    if (user?.group_id && item.label === 'Software Payment') {
      return false;
    }
    // SuperAdmin sees everything else
    if (isSuperAdmin || user?.role === 'SuperAdmin') return true;
    return isMenuVisible(item.label);
  }).map(item => {
    // Filter submenu items that belong to disabled modules (e.g. Library Report inside Reports)
    if (!item.submenu || isSuperAdmin || user?.role === 'SuperAdmin') return item;
    return { ...item, submenu: item.submenu.filter(sub => !sub.module || isModuleEnabled(sub.module)) };
  });
  
  // Prefix path with tenantCode if available
  const getPath = (path) => {
    if (tenantCode && !path.startsWith('/superadmin')) {
      return `/${tenantCode}${path}`;
    }
    return path;
  };

  const toggleSubmenu = (label) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const isActive = (path) => {
    const fullPath = getPath(path);
    return location.pathname === fullPath || location.pathname === path;
  };

  const isSubmenuActive = (submenu) => {
    return submenu?.some(item => isActive(item.path));
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-screen bg-slate-900 text-white transition-transform duration-300 overflow-y-auto
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          w-64 lg:w-64`}
      >
        {/* Logo & Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <img 
              src="/cloud-campus-logo.png" 
              alt="Cloud Campus Logo" 
              className="h-14 w-auto object-contain"
            />
          </div>
          <button onClick={toggleSidebar} className="lg:hidden">
            <X size={24} />
          </button>
        </div>

        {/* Main Menu */}
        <nav className="p-4 space-y-1">
          {/* SuperAdmin Section */}
          {(user?.role === 'SuperAdmin' || user?.role === 'superadmin' || user?.roles?.includes('SuperAdmin')) && (
            <div className="mb-3 pb-3 border-b border-slate-700">
              <div className="text-xs text-slate-400 px-3 mb-2 font-semibold uppercase">SuperAdmin</div>
              <Link
                to="/superadmin/tenants"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/tenants') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <Shield size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Tenant Management</span>
              </Link>
              <Link
                to="/superadmin/groups"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/groups') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <Building size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Group of Colleges</span>
              </Link>
              <Link
                to="/superadmin/combined-subscriptions"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/combined-subscriptions') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <TrendingUp size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Revenue Dashboard</span>
              </Link>
              <Link
                to="/superadmin/subscriptions"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/subscriptions') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <CreditCard size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Subscriptions</span>
              </Link>
              <Link
                to="/superadmin/subscription-plans"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/subscription-plans') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <DollarSign size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Subscription Plans</span>
              </Link>
              <Link
                to="/superadmin/public-plan-discounts"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/public-plan-discounts') ? 'bg-slate-800 text-blue-400' : ''}`}
                data-testid="sidebar-public-plan-discounts"
              >
                <DollarSign size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Public Plan Discounts</span>
              </Link>
              <Link
                to="/superadmin/trial-report"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/trial-report') ? 'bg-slate-800 text-blue-400' : ''}`}
                data-testid="sidebar-trial-report"
              >
                <DollarSign size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Trial & Purchase Report</span>
              </Link>
              <Link
                to="/superadmin/trial-report?tab=abandoned"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${location.search.includes('tab=abandoned') ? 'bg-slate-800 text-amber-400' : ''}`}
                data-testid="sidebar-abandoned-carts"
              >
                <ShoppingCart size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Abandoned Carts</span>
              </Link>
              <Link
                to="/superadmin/subscription-discount-report"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/subscription-discount-report') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <FileText size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Discount Report</span>
              </Link>
              <Link
                to="/superadmin/payment-gateway"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/payment-gateway') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <CreditCard size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Payment Gateway</span>
              </Link>
              <Link
                to="/superadmin/payment-requests"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/payment-requests') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <FileText size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Payment Requests</span>
              </Link>
              <Link
                to="/superadmin/email-settings"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/email-settings') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <Mail size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Email Settings</span>
              </Link>
              <Link
                to="/superadmin/whatsapp-settings"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/whatsapp-settings') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <MessageSquare size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">WhatsApp Settings</span>
              </Link>
              <Link
                to="/superadmin/data-migration"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/data-migration') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <Database size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Data Migration</span>
              </Link>
              <Link
                to="/superadmin/tickets"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/tickets') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <MessageSquare size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Support Tickets</span>
              </Link>
              <Link
                to="/superadmin/delete-logs"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/superadmin/delete-logs') ? 'bg-slate-800 text-red-400' : ''}`}
              >
                <Trash2 size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Delete Logs (Audit)</span>
              </Link>
            </div>
          )}

          {/* GroupAdmin Section */}
          {user?.role === 'GroupAdmin' && (
            <div className="mb-3 pb-3 border-b border-slate-700">
              <div className="text-xs text-slate-400 px-3 mb-2 font-semibold uppercase">Group Admin</div>
              <Link
                to="/group/dashboard"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/dashboard') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <Building size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Group Dashboard</span>
              </Link>
              <Link
                to="/group/colleges"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/colleges') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <Building size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">College Management</span>
              </Link>
              <Link
                to="/group/subscription"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/subscription') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <CreditCard size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">My Subscription</span>
              </Link>
              <div className="text-xs text-slate-400 px-3 mt-3 mb-2 font-semibold uppercase">Reports</div>
              <Link
                to="/group/admission-report"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/admission-report') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <Award size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Admission Report</span>
              </Link>
              <Link
                to="/group/collection-report"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/collection-report') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <DollarSign size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Collection Report</span>
              </Link>
              <Link
                to="/group/due-fee-report"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/due-fee-report') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <FileText size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Due Fee Report</span>
              </Link>
              <Link
                to="/group/student-attendance"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/student-attendance') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <GraduationCap size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Student Attendance</span>
              </Link>
              <Link
                to="/group/staff-attendance"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/staff-attendance') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <UserCheck size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Staff Attendance</span>
              </Link>
              <Link
                to="/group/salary-report"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/salary-report') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <Briefcase size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Salary Report</span>
              </Link>
              <Link
                to="/group/expenses-report"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/expenses-report') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <TrendingUp size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Expenses Report</span>
              </Link>
              <Link
                to="/group/income-expense-report"
                data-testid="group-nav-income-expense"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/income-expense-report') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <DollarSign size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Income &amp; Expenses</span>
              </Link>
              <Link
                to="/group/profit-loss"
                data-testid="group-nav-profit-loss"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/profit-loss') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <PieChart size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Profit &amp; Loss</span>
              </Link>
              <Link
                to="/group/balance-sheet"
                data-testid="group-nav-balance-sheet"
                className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                  ${isActive('/group/balance-sheet') ? 'bg-slate-800 text-blue-400' : ''}`}
              >
                <FileStack size={20} className="flex-shrink-0" />
                <span className="text-sm font-medium">Balance Sheet</span>
              </Link>
            </div>
          )}
          
          {filteredMenuItems.map((item, index) => (
            <div key={index}>
              {item.submenu ? (
                <div>
                  <button
                    onClick={() => toggleSubmenu(item.label)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-800 transition-colors
                      ${isSubmenuActive(item.submenu) ? 'bg-slate-800 text-blue-400' : ''}`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <item.icon size={20} className="flex-shrink-0" />
                      <span className="text-sm font-medium break-words">{item.label}</span>
                    </div>
                    {expandedMenus[item.label] ? <ChevronDown size={16} className="flex-shrink-0" /> : <ChevronRight size={16} className="flex-shrink-0" />}
                  </button>
                  {expandedMenus[item.label] && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.submenu.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={getPath(subItem.path)}
                          className={`block p-2 text-sm rounded-lg hover:bg-slate-800 transition-colors
                            ${isActive(subItem.path) ? 'bg-slate-800 text-blue-400' : 'text-slate-300'}`}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={getPath(item.path)}
                  className={`flex items-center gap-3 p-3 rounded-lg hover:bg-slate-800 transition-colors
                    ${isActive(item.path) ? 'bg-slate-800 text-blue-400' : ''}`}
                >
                  <item.icon size={20} className="flex-shrink-0" />
                  <span className="text-sm font-medium break-words">{item.label}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
